// components/SalesRevenue.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import Table from '../../shared/components/layout/Table';
import PaginationComponent from '../../shared/components/PaginationComponent';
import Button from '../../shared/components/ui/Button';
import NoDataFound from '../components/layout/NoDataFound';
// <-- The APIs
import {
  getBlockWiseRevenueReports,
  getUnitWiseRevenueReports
} from '../services/api/implementation/impl';

// <-- The same utility used in UsersList
import { transformData } from '../../super-admin/utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const SalesRevenue = ({
  subTab,
  selectedProjectId = null,
  onSelectedProjectIdChange
}) => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  /*
   * ----------------------------------------------
   * State & Refs
   * ----------------------------------------------
   */
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('Company');
  const applyFilterRef = useRef(null);
  const [checkedItems, setCheckedItems] = useState([]);

  const [blockPage, setBlockPage] = useState(1);
  const [unitPage, setUnitPage] = useState(1);
  const [limit] = useState(10); // items per page

  const [projectList, setProjectList] = useState([]);

  const { register } = useForm();

  /*
   * ----------------------------------------------
   * Fetch Projects
   * ----------------------------------------------
   */

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Revenue Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      setProjectList(projects);
    }
  }, [projects]);

  const handleProjectNameChange = (e) => {
    const v = e.target.value;
    onSelectedProjectIdChange?.(v === '' ? null : v);
  };

  /*
   * ----------------------------------------------
   * Fetch Revenue Data
   * ----------------------------------------------
   * Each tab calls a separate API with its own pagination state.
   */
  const { data: blockRevenueResponse, isFetching: isFetchingBlockRevenue } = useQuery({
    queryKey: ['blockRevenueReports', blockPage, limit, selectedProjectId],
    queryFn: () => getBlockWiseRevenueReports(blockPage, limit, selectedProjectId),
    keepPreviousData: true
  });

  const { data: unitRevenueResponse, isFetching: isFetchingUnitRevenue } = useQuery({
    queryKey: ['unitRevenueReports', unitPage, limit, selectedProjectId],
    queryFn: () => getUnitWiseRevenueReports(unitPage, limit, selectedProjectId),
    keepPreviousData: true
  });

  // Get data and meta information for each tab
  const blockRevenueData = blockRevenueResponse?.data || [];
  const blockTotalItems = blockRevenueResponse?.meta?.total || 0;
  const blockTotalPages = Math.ceil(blockTotalItems / limit);

  const unitRevenueData = unitRevenueResponse?.data || [];
  const unitTotalItems = unitRevenueResponse?.meta?.total || 0;
  const unitTotalPages = Math.ceil(unitTotalItems / limit);

  /*
   * ----------------------------------------------
   * Transform Data for the Table
   * ----------------------------------------------
   */
  let dataFormat = [];
  let tableHeaders = [];
  let processedRevenue = [];

  if (subTab === 'Blocks wise') {
    dataFormat = ['block', 'unitsSold', 'revenue', 'contribution', 'id'];
    tableHeaders = ['Block', 'Units Sold', 'Revenue', '% Revenue Contribution'];

    processedRevenue = blockRevenueData.map((item) => ({
      id: item?.id || 'no-id',
      block: item?.name || 'N/A',
      unitsSold: item?.unitSold || 0,
      revenue: `₹${(item?.revenue || 0).toLocaleString()}`,
      contribution: `${item?.revenueContribution || 0}%`
    }));
  } else {
    dataFormat = ['unitName', 'accommodationName', 'revenue', 'contribution', 'id'];
    tableHeaders = [
      'Unit Name',
      'Accommodation Name',
      'Revenue',
      '% Revenue Contribution'
    ];

    processedRevenue = unitRevenueData.map((item) => ({
      id: item?.id || 'no-id',
      unitName: item?.unitName || 'N/A',
      accommodationName: item?.accommodation_name || 'N/A',
      revenue: `₹${(item?.revenue || 0).toLocaleString()}`,
      contribution: item?.['% Revenue Contribution'] ?? 0
    }));
  }

  const transformedRevenueData = processedRevenue.map((row) => transformData(row, dataFormat));

  /*
   * ----------------------------------------------
   * Event Handlers
   * ----------------------------------------------
   */
  const handlePageChange = (newPage) => {
    if (subTab === 'Blocks wise') {
      setBlockPage(newPage);
    } else {
      setUnitPage(newPage);
    }
  };

  // Filter Widget
  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
    handleClearAll();
  };
  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
  };
  const handleCheckboxChange = (val) => {
    setCheckedItems((prev) =>
      prev.includes(val) ? prev.filter((item) => item !== val) : [...prev, val]
    );
  };
  const handleClearAll = () => {
    setCheckedItems([]);
    setSelectedFilter('Company');
  };

  // Close the filter widget if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (applyFilterRef.current && !applyFilterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
        handleClearAll();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /*
   * ----------------------------------------------
   * Rendering
   * ----------------------------------------------
   */
  return (
    <div className="z-5 flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-gray-100">
      {/* Filter Widget */}
      {isFilterOpen && (
        <div
          ref={applyFilterRef}
          className="absolute right-[2rem] mt-[7rem] w-[33rem] bg-white border rounded-lg shadow-lg z-[1000]">
          <div className="py-3 px-4 border-b flex justify-between items-center">
            <h4 className="font-semibold">Apply Filters</h4>
            <p
              onClick={handleClearAll}
              className="text-blue-500 underline hover:text-blue-700 transition duration-200 cursor-pointer">
              Clear all
            </p>
          </div>
          <div className="flex justify-between items-start">
            {/* Left Pane (Tabs) */}
            <div className="flex flex-col w-[30%]">
              <p
                className={`py-3 px-4 cursor-pointer border-b  ${
                  selectedFilter === 'Company' ? 'bg-[#d0edf1]' : ''
                }`}
                onClick={() => handleFilterClick('Company')}>
                Company
              </p>
              <p
                className={`py-3 px-4 cursor-pointer ${
                  selectedFilter === 'Month' ? 'bg-[#d0edf1]' : ''
                }`}
                onClick={() => handleFilterClick('Month')}>
                Month
              </p>
              <p
                className={`py-3 px-4 cursor-pointer border-t ${
                  selectedFilter === 'Block' ? 'bg-[#d0edf1]' : ''
                }`}
                onClick={() => handleFilterClick('Block')}>
                Block
              </p>
            </div>
            {/* Right Pane (Checkboxes) */}
            <div className="flex flex-col w-[70%] border-l">
              {selectedFilter === 'Company' && (
                <>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(1)}
                      onChange={() => handleCheckboxChange(1)}
                    />
                    Ved Builders
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(2)}
                      onChange={() => handleCheckboxChange(2)}
                    />
                    Nupur Constructions
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(9)}
                      onChange={() => handleCheckboxChange(9)}
                    />
                    LR Constructions
                  </div>
                </>
              )}
              {selectedFilter === 'Month' && (
                <div className="grid grid-cols-3 gap-1">
                  {[
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December'
                  ].map((month, index) => (
                    <div key={index} className="flex items-center gap-2 py-2 px-2 text-[#67676C]">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={checkedItems.includes(month)}
                        onChange={() => handleCheckboxChange(month)}
                      />
                      {month}
                    </div>
                  ))}
                </div>
              )}
              {selectedFilter === 'Block' && (
                <>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(3)}
                      onChange={() => handleCheckboxChange(3)}
                    />
                    Block A
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(4)}
                      onChange={() => handleCheckboxChange(4)}
                    />
                    Block B
                  </div>
                  <div className="flex items-center gap-2 py-3 px-4 text-[#67676C]">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checkedItems.includes(5)}
                      onChange={() => handleCheckboxChange(5)}
                    />
                    Block C
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Actions (Cancel/Apply) */}
          <div className="flex justify-end gap-4 p-6 border-t">
            <Button displayType="secondary" onClick={toggleFilter}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                /* Implement Apply Filter Logic */
              }}>
              Apply
            </Button>
          </div>
        </div>
      )}

      {/* Main Content — flex fills space below tabs (Sales Reports shell) */}
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {transformedRevenueData.length > 0 ? (
          <div className="mx-6 mb-4 mt-4 flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="relative h-full min-h-0 flex-1 overflow-hidden">
                <Table
                  headers={tableHeaders}
                  data={transformedRevenueData}
                  identifier="id"
                  isViewEnable={false}
                  isDeleteEnable={false}
                  isEditEnable={false}
                  isWrap={true}
                />
              </div>
              <div className="relative flex shrink-0 justify-center py-2">
                <PaginationComponent
                  page={subTab === 'Blocks wise' ? blockPage : unitPage}
                  totalPages={subTab === 'Blocks wise' ? blockTotalPages : unitTotalPages}
                  onPageChange={handlePageChange}
                  isFetching={
                    subTab === 'Blocks wise' ? isFetchingBlockRevenue : isFetchingUnitRevenue
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="pt-[3rem]">
            <NoDataFound disabledBtn screenHeight={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesRevenue;
